/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { PublicClientApplication } from "@azure/msal-browser";
import axios from "axios";
import {
	API_URL as baseApiUrl,
	APP_ENV,
	CLIENT_ID,
	CLIENT_SECRET,
} from "@/config/env";

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
const clientId = CLIENT_ID;

/**
 * Maps VITE_APP_ENV to API environment parameter
 */
const getEnvironmentParam = () => {
    const env = APP_ENV?.toUpperCase();
    if (env === "QA") return "UAT";
    if (env === "PROD" || env === "PRODUCTION") return "PROD";
    return "DEV"; // Default to DEV for DEV, QA, or any other value
};

/**
 * Fetches the client secret from the API and reverses it
 */
const fetchClientSecret = async () => {
    try {
        const environment = getEnvironmentParam();
        const apiUrl = `${baseApiUrl}GetSecret?environment=${environment}`;
        const response = await axios.get(apiUrl);
        const reversedSecret = response.data || "";
        
        if (!reversedSecret) {
            // Fallback to environment variable if API returns empty
            return CLIENT_SECRET || "";
        }

        // Reverse the string to get the original secret
        const secret = reversedSecret.split('').reverse().join('');
        return secret;
    } catch (error) {
        console.error("Error fetching client secret from API:", error);
        // Fallback to environment variable if API call fails
        return CLIENT_SECRET || "";
    }
};

/**
 * Creates MSAL config with the provided client secret
 */
const createMsalConfig = (clientSecret) => {
    return {
        auth: {
            clientId: clientId,
            authority: "https://login.microsoftonline.com/237582ad-3eab-4d44-8688-06ca9f2e613b",
            tenantid: "237582ad-3eab-4d44-8688-06ca9f2e613b",
            clientSecret: clientSecret,
            redirectUri: window.location.origin,
        },
    // auth: {
    //     clientId: "92082249-7471-4cfb-a47f-add6014c0451",
    //     authority: "https://login.microsoftonline.com/5989ece0-f90e-40bf-9c79-1a7beccdb861",
    //     tenantid: "5989ece0-f90e-40bf-9c79-1a7beccdb861",
    //     clientSecret: "",
    //     redirectUri: window.location.origin,
    // },
        cache: {
            cacheLocation: "sessionStorage", // This configures where your cache will be stored
            storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
        },
        system: {	
            loggerOptions: {	
                loggerCallback: (level, message, containsPii) => {	
                    if (containsPii) {		
                        return;		
                    }		
                    switch (level) {
                        case LogLevel.Error:
                            console.error(message);
                            return;
                        case LogLevel.Info:
                            console.info(message);
                            return;
                        case LogLevel.Verbose:
                            console.debug(message);
                            return;
                        case LogLevel.Warning:
                            console.warn(message);
                            return;
                        default:
                            return;
                    }	
                }	
            }	
        }
    };
};

// Cache for the MSAL instance promise
let msalInstancePromise = null;

/**
 * Gets or creates the MSAL instance with client secret fetched from API
 * Returns a promise that resolves to the MSAL instance
 */
export const getMsalInstance = async () => {
    if (!msalInstancePromise) {
        msalInstancePromise = (async () => {
            const clientSecret = await fetchClientSecret();
            const config = createMsalConfig(clientSecret);
            return new PublicClientApplication(config);
        })();
    }
    return msalInstancePromise;
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: ["User.Read","openid","profile","email"],
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};

export const protectedResourceMap = [
    ['https://graph.microsoft.com/v1.0/me', ['user.read']],
    ['https://graph.microsoft.com/v1.0/groups', ['group.read.all']]
  ]
